import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import { authAPI } from "services/api";
import OTPInput from "components/ui/OTPInput";
import CountdownTimer from "components/ui/CountdownTimer";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Email input, 2: OTP verification, 3: New password
  const [isLoading, setIsLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [email, setEmail] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      email: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const newPassword = watch("newPassword", "");
  const confirmPassword = watch("confirmPassword", "");

  // Step 1: Request password reset OTP
 const onSubmitEmail = async (data) => {
    setIsLoading(true);
    setOtpError("");

    try {
      const result = await authAPI.requestPasswordResetOtp(data.email);

      if (result.success) {
        setEmail(data.email);
        setCurrentStep(2); // Move to OTP verification step
        setTimerActive(true);
      } else {
        setError("email", {
          type: "manual",
          message: result.message || "Failed to send reset OTP. Please try again.",
        });
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("email", {
        type: "manual",
        message: "Failed to send reset OTP. Please check your connection and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Store the OTP as it's being entered
  const handleOtpChange = (newOtp) => {
    setOtp(newOtp);
    if (otpError) setOtpError("");
  };

  // Step 2: Verify OTP and move to new password step
  const handleVerifyOtp = async (otpValue) => {
    if (!otpValue || otpValue.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      // Verify the OTP with the backend
      const result = await authAPI.verifyPasswordResetOtp({
        email: email,
        otp: otpValue,
      });

      if (result.success) {
        // OTP verified, move to new password step
        setOtp(otpValue); // Store the verified OTP for the next step
        setCurrentStep(3);
      } else {
        setOtpError(result.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError("OTP verification failed. Please try again.");
    } finally {
      setOtpLoading(false);
    }
 };

  // Step 3: Set new password
  const onSubmitNewPassword = async (data) => {
    setIsLoading(true);
    setOtpError("");

    if (data.newPassword !== data.confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });
      setIsLoading(false);
      return;
    }

    // In a real implementation, we would call the API to reset the password
    // For now, we'll just redirect to login with success message
    try {
      // Note: In a real implementation, we would use the verified OTP from step 2
      // along with the new password to call the API
      const result = await authAPI.verifyPasswordResetOtp({
        email: email,
        otp: otp, // This should be the OTP that was verified in step 2
        newPassword: data.newPassword,
      });

      if (result.success) {
        // Redirect to login with success message
        navigate("/login", { state: { passwordResetSuccess: true } });
      } else {
        setOtpError(result.message || "Failed to reset password. Please try again.");
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setOtpError("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

   // We need to store the OTP that was entered and verified
  const [otp, setOtp] = useState("");

  // Resend OTP
  const handleResendOtp = async () => {
    setIsLoading(true);
    setOtpError("");

    try {
      const result = await authAPI.requestPasswordResetOtp(email);

      if (result.success) {
        setTimerActive(true);
        setOtpError("New OTP sent to your email.");
      } else {
        setOtpError(result.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle timer completion
  const handleTimerComplete = () => {
    setTimerActive(false);
    setOtpError("OTP has expired. Please request a new one.");
  };

  // Password validation
 const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value?.length < 8) return "Password must be at least 8 characters";
    if (!/(?=.*[a-z])/?.test(value))
      return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/?.test(value))
      return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/?.test(value))
      return "Password must contain at least one number";
    return true;
  };

  const validateConfirmPassword = (value) => {
    if (!value) return "Please confirm your password";
    if (value !== newPassword) return "Passwords do not match";
    return true;
 };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let score = 0;
    if (password?.length >= 8) score++;
    if (/(?=.*[a-z])/?.test(password)) score++;
    if (/(?=.*[A-Z])/?.test(password)) score++;
    if (/(?=.*\d)/?.test(password)) score++;
    if (/(?=.*[@$!%*?&])/?.test(password)) score++;

    const strengthMap = {
      0: { strength: 0, label: "", color: "" },
      1: { strength: 20, label: "Very Weak", color: "bg-red-500" },
      2: { strength: 40, label: "Weak", color: "bg-orange-500" },
      3: { strength: 60, label: "Fair", color: "bg-yellow-500" },
      4: { strength: 80, label: "Good", color: "bg-blue-500" },
      5: { strength: 100, label: "Strong", color: "bg-green-500" },
    };

    return strengthMap?.[score];
  };

  const passwordStrength = getPasswordStrength(newPassword);

  if (currentStep === 1) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
            <Icon name="Mail" size={32} color="white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Forgot Password?
          </h1>
          <p className="text-muted-foreground">
            Enter your email address and we'll send you an OTP to reset your password
          </p>
        </div>
        
        <form onSubmit={handleSubmit(onSubmitEmail)} className="space-y-6">
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email"
            error={errors?.email?.message}
            required
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Please enter a valid email address",
              },
            })}
          />

          {/* Error Message */}
          {errors?.email && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <Icon
                  name="AlertCircle"
                  size={20}
                  className="text-red-600 mr-2"
                />
                <span className="text-red-70 text-sm">
                  {errors?.email?.message}
                </span>
              </div>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? "Sending OTP..." : "Send Reset OTP"}
          </Button>

          <div className="text-center">
            <p className="text-muted-foreground text-sm">
              Remember your password?{" "}
              <Link
                to="/login"
                className="text-primary hover:text-primary/80 font-medium transition-micro"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
            <Icon name="ShieldCheck" size={32} color="white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Verify Your Email
          </h1>
          <p className="text-muted-foreground">
            We've sent a 6-digit code to <span className="font-medium">{email}</span>
          </p>
        </div>

        <div className="space-y-6">
          {/* OTP Input */}
          <OTPInput
            length={6}
            onChange={handleOtpChange}
            onComplete={handleVerifyOtp}
            error={otpError}
            isLoading={otpLoading}
          />

          {/* Timer */}
          {timerActive && (
            <CountdownTimer
              initialMinutes={5}
              onComplete={handleTimerComplete}
              isRunning={timerActive}
            />
          )}

          {/* Error Message */}
          {otpError && !timerActive && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <Icon
                  name="AlertCircle"
                  size={20}
                  className="text-red-600 mr-2"
                />
                <span className="text-red-70 text-sm">
                  {otpError}
                </span>
              </div>
            </div>
          )}

          {/* Resend OTP Button */}
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={handleResendOtp}
            disabled={isLoading || timerActive}
            loading={isLoading}
          >
            {isLoading ? "Sending..." : "Resend OTP"}
          </Button>

          {/* Back Button */}
          <Button
            variant="ghost"
            size="lg"
            fullWidth
            onClick={() => setCurrentStep(1)}
          >
            Back to Email
          </Button>
        </div>
      </div>
    );
  }

  if (currentStep === 3) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
            <Icon name="Lock" size={32} color="white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Create New Password
          </h1>
          <p className="text-muted-foreground">
            Enter your new password below
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitNewPassword)} className="space-y-6">
          {/* New Password */}
          <div className="relative">
            <Input
              label="New Password"
              type={showPassword ? "text" : "password"}
              placeholder="Create a strong password"
              error={errors?.newPassword?.message}
              required
              {...register("newPassword", {
                validate: validatePassword,
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>

          {/* Password Strength Indicator */}
          {newPassword && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Password Strength</span>
                <span
                  className={`font-medium ${
                    passwordStrength?.strength >= 80
                      ? "text-green-600"
                      : passwordStrength?.strength >= 60
                      ? "text-blue-600"
                      : passwordStrength?.strength >= 40
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  {passwordStrength?.label}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${passwordStrength?.color}`}
                  style={{ width: `${passwordStrength?.strength}%` }}
                />
              </div>
            </div>
          )}

          {/* Confirm Password */}
          <div className="relative">
            <Input
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your new password"
              error={errors?.confirmPassword?.message}
              required
              {...register("confirmPassword", {
                validate: validateConfirmPassword,
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>

          {/* Error Message */}
          {otpError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <Icon
                  name="AlertCircle"
                  size={20}
                  className="text-red-600 mr-2"
                />
                <span className="text-red-70 text-sm">
                  {otpError}
                </span>
              </div>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? "Resetting Password..." : "Reset Password"}
          </Button>

          <div className="text-center">
            <p className="text-muted-foreground text-sm">
              Remember your password?{" "}
              <Link
                to="/login"
                className="text-primary hover:text-primary/80 font-medium transition-micro"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </div>
    );
  }

  return null;
};

export default ForgotPassword;
